/**
 * A doc's body, read (slice 2d spec §10.7): Streamdown with GitHub-flavoured
 * markdown, as replies are, with a doc's rules. Raw HTML isn't rendered (no
 * `rehype-raw`, and `skipHtml`); links open in a new tab; task lists show
 * their boxes, not tickable; tables scroll sideways, as a reply's do. An image by URL is
 * never loaded (decision 5): it shows as a link with its alt text and host,
 * since a loaded image would tell its host who read the doc.
 */
import { cn } from "@crew/ui/lib/utils";
import { Image } from "lucide-react";
import type { ComponentProps } from "react";
import {
  defaultRehypePlugins,
  defaultRemarkPlugins,
  Streamdown,
  type Components,
} from "streamdown";
import { MarkdownTable } from "../chats/markdown-table";
import { hostOf } from "./doc-view";

type Node = { type: string; value?: string; children?: Node[] };

/**
 * HTML in a doc shows as the text that was written (Streamdown already does
 * this for a tag: `use the <Vault> page` keeps its `<Vault>`), except that a
 * `<br>` is a line break, the usual way to break a table cell, and a comment
 * is hidden. Nothing is ever rendered as HTML.
 */
function remarkHtmlAsText() {
  const visit = (node: Node) => {
    node.children = node.children?.flatMap((child): Node[] => {
      if (child.type !== "html") {
        visit(child);
        return [child];
      }
      const value = child.value ?? "";
      if (/^<!--[\s\S]*-->$/.test(value.trim())) return [];
      if (/^<br\s*\/?>$/i.test(value.trim())) return [{ type: "break" }];
      return [{ type: "text", value }];
    });
  };
  return (tree: Node) => visit(tree);
}

const REMARK = [...Object.values(defaultRemarkPlugins), remarkHtmlAsText];

/** Every default but `raw`: sanitising and hardening stay. */
const REHYPE = Object.entries(defaultRehypePlugins)
  .filter(([name]) => name !== "raw")
  .map(([, plugin]) => plugin);

function DocLinkTag({
  href,
  children,
  node: _node,
  ...props
}: ComponentProps<"a"> & { node?: unknown }) {
  return (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-foreground underline underline-offset-2"
    >
      {children}
    </a>
  );
}

/** An image by URL, as a link: its alt text and host (decision 5). Nothing is fetched. */
function ImageAsLink({ src, alt }: { src?: unknown; alt?: string }) {
  const url = typeof src === "string" ? src : "";
  const label = alt?.trim() || "Image";
  const host = url ? hostOf(url) : "";
  const body = (
    <>
      <Image aria-hidden className="size-3.5 shrink-0" />
      <span>{label}</span>
      {host ? <span className="text-muted-foreground">({host})</span> : null}
    </>
  );
  const className =
    "inline-flex items-baseline gap-1.5 rounded-md bg-muted px-1.5 py-0.5 text-[0.95em] [&_svg]:self-center";
  if (!/^https?:\/\//i.test(url)) {
    return (
      <span data-doc-image className={className}>
        {body}
      </span>
    );
  }
  return (
    <a
      data-doc-image
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(className, "text-foreground underline-offset-2 hover:underline")}
    >
      {body}
    </a>
  );
}

/** Task list boxes: shown as they're written, never ticked from here (a tick would be a write). */
function Checkbox({
  type,
  checked,
  node: _node,
  ...props
}: ComponentProps<"input"> & { node?: unknown }) {
  if (type !== "checkbox") return <input type={type} {...props} />;
  return (
    <input
      type="checkbox"
      checked={Boolean(checked)}
      disabled
      readOnly
      aria-readonly
      className="mr-1.5 size-3.5 translate-y-[2px] accent-foreground"
    />
  );
}

const COMPONENTS: Components = {
  a: DocLinkTag,
  img: ImageAsLink as Components["img"],
  input: Checkbox,
  // Tables keep readable columns and scroll sideways on a phone (48d), as a reply's do.
  table: MarkdownTable,
};

export function DocMarkdown({ children, className }: { children: string; className?: string }) {
  return (
    <Streamdown
      mode="static"
      className={cn(
        "space-y-3.5 text-[15px] leading-6 text-copy [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground",
        // A task list's items show their box, not a bullet too.
        "[&_li:has(>input)]:list-none [&_ul:has(>li>input)]:pl-0",
        className,
      )}
      controls={false}
      skipHtml
      remarkPlugins={REMARK}
      rehypePlugins={REHYPE}
      components={COMPONENTS}
      lineNumbers={false}
    >
      {children}
    </Streamdown>
  );
}
